import React, { useState, useRef, useEffect } from 'react';
import { Chart as ChartJS, registerables } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import SortableTable from './SortableTable';
import LoadingSpinner from './LoadingSpinner';
import { API_BASE_URL } from '../config';

// Add CSS for line clamping and viewport height if not already available
const addCustomStyles = () => {
  if (!document.getElementById('ai-streaming-styles')) {
    const style = document.createElement('style');
    style.id = 'ai-streaming-styles';
    style.textContent = `
      .line-clamp-2 {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      .ai-container-height {
        height: calc(100vh - 5rem);
        min-height: 600px;
      }
      @media (max-height: 700px) {
        .ai-container-height {
          height: calc(100vh - 4rem);
          min-height: 500px;
        }
      }
    `;
    document.head.appendChild(style);
  }
};

// Register Chart.js components
ChartJS.register(...registerables);

// Hook utilitaire pour détecter le mode mobile
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [breakpoint]);
  return isMobile;
}

const AIStreamingQuery = ({ fetchWithAuth }) => {
  const [question, setQuestion] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [streamingSteps, setStreamingSteps] = useState([]);
  const [finalResult, setFinalResult] = useState(null);
  const [error, setError] = useState(null);
  const [queryHistory, setQueryHistory] = useState([]);
  const [pinnedQueries, setPinnedQueries] = useState([]);
  const [expandedSteps, setExpandedSteps] = useState(new Set());
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [chartData, setChartData] = useState(null);
  const [chartConfig, setChartConfig] = useState(null);
  const [rawData, setRawData] = useState(null);
  const [executedQuery, setExecutedQuery] = useState('');
  const [queryDetails, setQueryDetails] = useState(null);
  const [abortController, setAbortController] = useState(null);

  const eventSourceRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Hook pour détecter le mode mobile
  const isMobile = useIsMobile();

  // Load query history and pinned queries from localStorage on mount
  useEffect(() => {
    addCustomStyles();
    const savedHistory = localStorage.getItem('ai_query_history');
    const savedPinned = localStorage.getItem('ai_pinned_queries');

    if (savedHistory) {
      try {
        setQueryHistory(JSON.parse(savedHistory));
      } catch (error) {
        console.error('Error loading query history:', error);
      }
    }

    if (savedPinned) {
      try {
        setPinnedQueries(JSON.parse(savedPinned));
      } catch (error) {
        console.error('Error loading pinned queries:', error);
      }
    }
  }, []);

  // Save query history to localStorage whenever it changes
  useEffect(() => {
    if (queryHistory.length > 0) {
      localStorage.setItem('ai_query_history', JSON.stringify(queryHistory));
    }
  }, [queryHistory]);

  // Save pinned queries to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('ai_pinned_queries', JSON.stringify(pinnedQueries));
  }, [pinnedQueries]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

    const processStreamEvent = async (data) => {
    try {
      const parsedData = JSON.parse(data);
      console.log('📨 [AI Streaming] Processing event in real-time:', {
        step: parsedData.step,
        status: parsedData.status,
        message: parsedData.message,
        timestamp: new Date().toISOString(),
        hasData: !!parsedData.data
      });

      // Process immediately for real-time updates
      if (parsedData.step === 'final_result') {
        console.log('🎯 [AI Streaming] Final result received:', {
          hasData: !!parsedData.data?.data,
          dataLength: parsedData.data?.data?.length,
          hasChartData: !!parsedData.data?.chart_data,
          hasSqlQuery: !!parsedData.data?.sql_query
        });
        setFinalResult(parsedData.data);
        // Add successful query to history
        addToHistory(question.trim(), parsedData.data);
      } else if (parsedData.step === 'error') {
        console.error('❌ [AI Streaming] Error received:', parsedData);
        setError(parsedData);
        setIsProcessing(false);
        return;
      } else {
        setStreamingSteps(prev => {
          const existingIndex = prev.findIndex(step => step.step === parsedData.step);
          if (existingIndex >= 0) {
            const updated = [...prev];
            updated[existingIndex] = { ...updated[existingIndex], ...parsedData };
            console.log('🔄 [AI Streaming] Updated existing step:', parsedData.step);
            return updated;
          }
          console.log('➕ [AI Streaming] Added new step:', parsedData.step);
          return [...prev, parsedData];
        });
      }
    } catch (parseError) {
      console.error('❌ [AI Streaming] Error parsing streaming data:', parseError, 'Raw data:', data);
    }
  };

  const addToHistory = (query, result = null) => {
    const historyItem = {
      id: Date.now(),
      question: query,
      timestamp: new Date().toISOString(),
      result: result,
      hasChart: result?.chart_data ? true : false,
      rowCount: result?.data?.length || 0
    };

    setQueryHistory(prev => [historyItem, ...prev.slice(0, 19)]); // Keep last 20 queries
  };

  const loadFromHistory = (historyItem) => {
    if (isProcessing) return;

    setQuestion(historyItem.question);
    if (historyItem.result) {
      setFinalResult(historyItem.result);
      setStreamingSteps([]);
      setError(null);
    }
  };

  const clearHistory = () => {
    setQueryHistory([]);
    localStorage.removeItem('ai_query_history');
  };

  const clearPinnedQueries = () => {
    setPinnedQueries([]);
    localStorage.removeItem('ai_pinned_queries');
  };

  const clearNotPinnedQueries = () => {
    const clearedHistory = queryHistory.filter(item => !pinnedQueries.some(pinned => pinned.id === item.id));
    setQueryHistory(clearedHistory);
    localStorage.setItem('ai_query_history', JSON.stringify(clearedHistory));

    console.log('🗑️ [AI Streaming] Cleared not pinned queries:', pinnedQueries, clearedHistory);
  };

  const togglePin = (item) => {
    const isPinned = pinnedQueries.some(pinned => pinned.id === item.id);

    if (isPinned) {
      // Unpin: remove from pinned queries
      setPinnedQueries(prev => prev.filter(pinned => pinned.id !== item.id));
    } else {
      // Pin: add to pinned queries (max 10 pinned items)
      setPinnedQueries(prev => [item, ...prev.slice(0, 9)]);
    }
  };

  const isPinned = (item) => {
    return pinnedQueries.some(pinned => pinned.id === item.id);
  };

  const toggleStepExpansion = (stepName) => {
    setExpandedSteps(prev => {
      const newSet = new Set(prev);
      if (newSet.has(stepName)) {
        newSet.delete(stepName);
      } else {
        newSet.add(stepName);
      }
      return newSet;
    });
  };

  // Detect if user is on Mac for keyboard shortcut display
  const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  const handleKeyDown = (e) => {
    // Support Cmd+Enter (Mac) or Ctrl+Enter (Windows/Linux) to submit
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!question.trim() || isProcessing) return;

    const currentQuestion = question.trim();

    // Reset state
    setIsProcessing(true);
    setStreamingSteps([]);
    setFinalResult(null);
    setError(null);
    setExpandedSteps(new Set());

    console.log('🚀 [AI Streaming] Starting query:', currentQuestion);

    try {
      // Create abort controller for cleanup
      abortControllerRef.current = new AbortController();

      const response = await fetchWithAuth(`${API_BASE_URL}/api/ai/query-stream`, {
        method: 'POST',
        body: JSON.stringify({ question: currentQuestion }),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      console.log('✅ [AI Streaming] Connection established, starting to read stream...');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      const processStream = async () => {
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            console.log('🏁 [AI Streaming] Stream ended');
            setIsProcessing(false);
            break;
          }

          const chunk = decoder.decode(value, { stream: true });
          buffer += chunk;

          console.log('📦 [AI Streaming] Received chunk at', new Date().toISOString(), '- Size:', chunk.length);

          // Process complete lines immediately
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const data = line.slice(6).trim();

              if (data === '[DONE]') {
                console.log('✅ [AI Streaming] Received [DONE] signal');
                setIsProcessing(false);
                return;
              }

              if (data) {
                await processStreamEvent(data);
              }
            }
          }
        }
      };

      await processStream();
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('❌ [AI Streaming] Connection error:', error);
        setError({
          step: 'connection_error',
          status: 'failed',
          message: error.message
        });
        // Add failed query to history
        addToHistory(currentQuestion, null);
      }
      setIsProcessing(false);
    }
  };

  const stopProcessing = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setIsProcessing(false);
  };

  // Generate columns automatically from data
  const generateColumns = (data) => {
    if (!data || data.length === 0) {
      console.log('⚠️ [AI Streaming] No data provided for column generation');
      return [];
    }

    const firstRow = data[0];
    if (!firstRow || typeof firstRow !== 'object') {
      console.log('⚠️ [AI Streaming] Invalid data structure for column generation:', firstRow);
      return [];
    }

    const columns = Object.keys(firstRow).map(key => ({
      key,
      label: key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, ' '),
      sortable: true,
      filterable: true
    }));

    console.log('📊 [AI Streaming] Generated columns:', columns);
    return columns;
  };

  const getStepIcon = (step, status) => {
    if (status === 'running') return '⏳';
    if (status === 'completed') return '✅';
    if (status === 'failed' || status === 'error') return '❌';

    switch (step) {
      case 'analysis': return '🧠';
      case 'sql_generation': return '📝';
      case 'query_execution':
      case 'execution': return '⚡';
      case 'error_analysis': return '🔍';
      case 'retry_preparation': return '🔄';
      case 'validation': return '🔍';
      case 'chart_validation': return '📊';
      case 'chart_preparation': return '📈';
      case 'multi_query_planning': return '📋';
      case 'multi_query_execution': return '🔄';
      case 'data_merge':
      case 'data_merging': return '🔗';
      case 'complex_analysis': return '🤖';
      case 'completion': return '🎉';
      default: return '📌';
    }
  };

  const getStepTitle = (step) => {
    const titles = {
      analysis: 'Analyse de la question',
      sql_generation: 'Génération SQL',
      query_execution: 'Exécution de la requête',
      execution: 'Exécution de la requête',
      error_analysis: 'Analyse d\'erreur',
      retry_preparation: 'Préparation retry',
      validation: 'Validation des résultats',
      chart_validation: 'Validation du graphique',
      chart_preparation: 'Préparation du graphique',
      multi_query_planning: 'Planification multi-requêtes',
      multi_query_execution: 'Exécution multi-requêtes',
      data_merge: 'Fusion des données',
      data_merging: 'Fusion des données',
      complex_analysis: 'Analyse complexe',
      completion: 'Finalisation'
    };
    return titles[step] || step;
  };

  // Skeleton component for loading state
  const ResultSkeleton = () => (
    <div className="space-y-6">
      {/* Chart/Table Skeleton */}
      <div className="bg-white dark:bg-gray-700 rounded-xl shadow-sm border border-gray-200 dark:border-gray-600 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-600 rounded-lg w-48 mb-4"></div>
          <div className="h-64 bg-gray-100 dark:bg-gray-600 rounded-xl"></div>
        </div>
      </div>

      {/* Data Table Skeleton */}
      <div className="bg-white dark:bg-gray-700 rounded-xl shadow-sm border border-gray-200 dark:border-gray-600 p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-200 dark:bg-gray-600 rounded-lg w-32 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-4 bg-gray-100 dark:bg-gray-600 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    </div>
      );

  // Mobile layout
  if (isMobile) {
    return (
      <div className="bg-gray-50 dark:bg-gray-900 min-h-screen flex flex-col">
        {/* Prompt input full width */}
        <div className="p-4 bg-white dark:bg-gray-800 shadow-md sticky top-0 z-20 flex items-center gap-2">
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col gap-2">
            <textarea
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ex: 'Montre-moi un graphique des ventes par marque ce mois-ci'"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm shadow-sm"
              rows={3}
              disabled={isProcessing}
            />
            <div className="flex gap-2 mt-1">
              <button
                type="submit"
                disabled={!question.trim() || isProcessing}
                className="flex-1 px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-medium shadow-lg hover:shadow-xl transition-all duration-200"
              >
                {isProcessing ? <><LoadingSpinner size="xs" />Traitement...</> : <>🚀 Analyser</>}
              </button>
              {isProcessing && (
                <button
                  type="button"
                  onClick={stopProcessing}
                  className="px-4 py-2 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-lg text-sm font-medium shadow-lg hover:shadow-xl transition-all duration-200"
                >⏹️</button>
              )}
            </div>
          </form>
          {/* Button to open history modal */}
          <button
            className="ml-2 p-2 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500"
            onClick={() => setShowHistoryModal(true)}
            title="Afficher l'historique"
          >
            <span className="sr-only">Afficher l'historique</span>
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          </button>
        </div>

        {/* Mobile History Modal */}
        {showHistoryModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-md mx-2 p-4 max-h-[90vh] flex flex-col">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-base">📚 Historique</h3>
                <button onClick={() => setShowHistoryModal(false)} className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700">
                  <span className="sr-only">Fermer</span>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
              <div className="flex-1 overflow-y-auto">
                {pinnedQueries.length === 0 && queryHistory.length === 0 ? (
                  <div className="p-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                    <div className="text-4xl mb-2">📝</div>
                    <p>Aucun historique</p>
                    <p className="text-xs mt-1">Vos requêtes s'afficheront ici</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pinnedQueries.length > 0 && (
                      <>
                        <div className="flex items-center gap-2 mb-3">
                          <div className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                            📌 Épinglées ({pinnedQueries.length})
                          </div>
                          <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600"></div>
                        </div>
                        {pinnedQueries.map((item) => (
                          <div key={`pinned-${item.id}`} className="relative group p-3 rounded-xl border-2 border-yellow-300 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md" onClick={() => { loadFromHistory(item); setShowHistoryModal(false); }}>
                            <div className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1 line-clamp-2">{item.question}</div>
                            <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                              <span className="flex items-center gap-1">🕒 {new Date(item.timestamp).toLocaleDateString('fr-FR')}</span>
                              <div className="flex items-center gap-2">
                                {item.hasChart && <span className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 px-2 py-1 rounded-full text-xs">📊 Graph</span>}
                                {item.rowCount > 0 && <span className="bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 px-2 py-1 rounded-full text-xs">{item.rowCount} lignes</span>}
                              </div>
                            </div>
                            <button onClick={e => { e.stopPropagation(); togglePin(item); }} className="absolute top-2 right-2 opacity-100 text-yellow-600 dark:text-yellow-400 p-1 hover:bg-yellow-200 dark:hover:bg-yellow-800 rounded-full" title="Désépingler">📌</button>
                          </div>
                        ))}
                        {queryHistory.length > 0 && (
                          <div className="flex items-center gap-2 my-4">
                            <div className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">📋 Récentes ({queryHistory.length})</div>
                            <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600"></div>
                          </div>
                        )}
                      </>
                    )}
                    {queryHistory.map((item) => (
                      <div key={item.id} className="relative group p-3 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md" onClick={() => { loadFromHistory(item); setShowHistoryModal(false); }}>
                        <div className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-1 line-clamp-2">{item.question}</div>
                        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                          <span className="flex items-center gap-1">🕒 {new Date(item.timestamp).toLocaleDateString('fr-FR')}</span>
                          <div className="flex items-center gap-2">
                            {item.hasChart && <span className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 px-2 py-1 rounded-full text-xs">📊 Graph</span>}
                            {item.rowCount > 0 && <span className="bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 px-2 py-1 rounded-full text-xs">{item.rowCount} lignes</span>}
                          </div>
                        </div>
                        <button onClick={e => { e.stopPropagation(); togglePin(item); }} className={`absolute top-2 right-2 transition-opacity p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full ${isPinned(item) ? 'opacity-100 text-yellow-600 dark:text-yellow-400' : 'opacity-0 group-hover:opacity-100 text-gray-400 dark:text-gray-500'}`} title={isPinned(item) ? "Désépingler" : "Épingler"}>📌</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="mt-3 flex gap-2 justify-end">
                {pinnedQueries.length > 0 && (
                  <button onClick={clearPinnedQueries} className="text-xs text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400">Effacer épinglées</button>
                )}
                {queryHistory.length > 0 && (
                  <button onClick={clearHistory} className="text-xs text-gray-500 hover:text-red-600 dark:text-gray-400 dark:hover:text-red-400">Effacer tout</button>
                )}
              </div>
            </div>
          </div>
        )}

                {/* Réponse et détails IA */}
        <div className="flex-1 flex flex-col gap-4 p-4 overflow-y-auto">
          {isProcessing && !finalResult && <ResultSkeleton />}

          {/* Final Results */}
          {finalResult && (
            <div className="space-y-6">
              {/* Chart Display */}
              {finalResult.chart_data && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
                    📊 Visualisation
                  </h3>
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-xl p-4" style={{ height: '300px' }}>
                    <Chart {...finalResult.chart_data.config} />
                  </div>
                </div>
              )}

              {/* Data Table */}
              {finalResult.data && finalResult.data.length > 0 && (() => {
                const columns = generateColumns(finalResult.data);

                if (columns.length === 0) {
                  return (
                    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
                      <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
                        📋 Données ({finalResult.data.length} lignes)
                      </h3>
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <p>Impossible de générer les colonnes pour afficher les données.</p>
                        <pre className="mt-4 text-xs bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 p-4 rounded-xl overflow-auto text-left">
                          {JSON.stringify(finalResult.data.slice(0, 2), null, 2)}
                        </pre>
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                    <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                          📋 Données ({finalResult.data.length} lignes)
                        </h3>
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {finalResult.metadata?.execution_time_ms && (
                            <span className="bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 px-2 py-1 rounded-full text-xs">
                              ⚡ {finalResult.metadata.execution_time_ms}ms
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <SortableTable
                        data={finalResult.data}
                        columns={columns}
                        exportable={true}
                        exportFilename={`ai_query_${Date.now()}.csv`}
                        exportRenamable={true}
                        fullHeight={false}
                        globalSearch={true}
                      />
                    </div>
                  </div>
                );
              })()}

              {/* Show SQL queries if available */}
              {finalResult.sql_queries && finalResult.sql_queries.length > 0 && (
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6 border border-gray-200 dark:border-gray-700">
                  <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4 flex items-center gap-2">
                    🔍 Requêtes SQL générées
                  </h3>
                  <div className="space-y-3">
                    {finalResult.sql_queries.map((query, index) => (
                      <div key={index}>
                        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Requête {index + 1}/{finalResult.sql_queries.length}:
                        </div>
                        <pre className="bg-gray-100 dark:bg-gray-900 text-gray-800 dark:text-gray-200 p-3 rounded-lg text-sm overflow-x-auto border">
                          <code>{query}</code>
                        </pre>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-red-600 dark:text-red-400 text-xl">❌</span>
                <h3 className="font-semibold text-red-800 dark:text-red-400">Erreur</h3>
              </div>
              <p className="text-red-700 dark:text-red-300 text-sm">{error.message}</p>
            </div>
          )}

          {/* Progress Steps for Mobile */}
          {(isProcessing || streamingSteps.length > 0) && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-4 border border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between mb-3">
                <h4 className="font-medium text-gray-800 dark:text-gray-200 text-sm flex items-center gap-2">
                  ⚡ Progression
                </h4>
                <button
                  onClick={() => toggleStepExpansion('progress_details')}
                  className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
                >
                  {expandedSteps.has('progress_details') ? '👁️ Masquer détails' : '🔍 Voir détails'}
                </button>
              </div>

              {/* Compact Progress Steps */}
              <div className="flex items-center gap-2 mb-3 overflow-x-auto">
                {(() => {
                  const receivedSteps = streamingSteps.map(s => s.step);
                  let stepsToShow = ['analysis'];

                  if (receivedSteps.includes('sql_generation')) {
                    stepsToShow = ['analysis', 'sql_generation', 'execution', 'completion'];
                  } else if (receivedSteps.includes('multi_query_planning')) {
                    stepsToShow = ['analysis', 'multi_query_planning', 'multi_query_execution', 'data_merging', 'completion'];
                  } else if (receivedSteps.includes('complex_analysis')) {
                    stepsToShow = ['analysis', 'complex_analysis', 'execution', 'completion'];
                  } else {
                    stepsToShow = ['analysis', 'execution', 'completion'];
                  }

                  return stepsToShow.map((stepName, index) => {
                    const step = streamingSteps.find(s => s.step === stepName);
                    const isActive = step?.status === 'running';
                    const isCompleted = step?.status === 'completed';
                    const isFailed = step?.status === 'failed';

                    return (
                      <div key={stepName} className="flex items-center flex-shrink-0">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium shadow-sm transition-all duration-300 ${
                          isFailed ? 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400 border-2 border-red-300 dark:border-red-700' :
                          isCompleted ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 border-2 border-green-300 dark:border-green-700' :
                          isActive ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 border-2 border-blue-300 dark:border-blue-700 animate-pulse' :
                          'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-2 border-gray-200 dark:border-gray-600'
                        }`}>
                          {isFailed ? '✕' : isCompleted ? '✓' : isActive ? '⏳' : index + 1}
                        </div>
                        {index < stepsToShow.length - 1 && (
                          <div className={`w-6 h-1 rounded-full transition-all duration-300 ${
                            isCompleted ? 'bg-green-300 dark:bg-green-600' : 'bg-gray-200 dark:bg-gray-600'
                          }`}></div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>

              {/* Current Step Message */}
              {streamingSteps.length > 0 && (
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  {streamingSteps[streamingSteps.length - 1]?.message}
                </div>
              )}

              {/* Detailed Steps - Expandable */}
              {expandedSteps.has('progress_details') && streamingSteps.length > 0 && (
                <div className="space-y-2 max-h-48 overflow-y-auto bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                  {streamingSteps.map((step, index) => (
                    <div key={`${step.step}-${index}`} className="flex items-start gap-2 text-sm bg-white dark:bg-gray-700 p-2 rounded-lg shadow-sm">
                      <span className="text-base">{getStepIcon(step.step, step.status)}</span>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-800 dark:text-gray-200 text-sm">{getStepTitle(step.step)}</div>
                        <div className="text-gray-600 dark:text-gray-400 text-xs">{step.message}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop layout (original)
  return (
    <div className="flex bg-gray-50 dark:bg-gray-900 gap-4 p-4 ai-container-height">
      {/* Left Column - Input & History */}
      <div className="w-1/3 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden">

        {/* Query Input */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ex: 'Montre-moi un graphique des ventes par marque ce mois-ci'"
                className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none text-sm shadow-sm"
                rows={4}
                disabled={isProcessing}
              />
              <div className="mt-2 text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <span>💡</span>
                <span>Astuce:
                  <kbd className="mx-1 px-1 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-xs font-mono">
                    {isMac ? '⌘' : 'Ctrl'}
                  </kbd>
                  +
                  <kbd className="mx-1 px-1 py-0.5 bg-gray-200 dark:bg-gray-600 rounded text-xs font-mono">
                    Entrée
                  </kbd>
                  pour soumettre
                </span>
              </div>
            </div>
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={!question.trim() || isProcessing}
                className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-medium shadow-lg hover:shadow-xl transition-all duration-200"
              >
                {isProcessing ? (
                  <>
                    {/* <LoadingSpinner size="xs" /> */}
                    Traitement...
                  </>
                ) : (
                  <>
                    🚀 Analyser
                  </>
                )}
              </button>
              {isProcessing && (
                <button
                  type="button"
                  onClick={stopProcessing}
                  className="px-4 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 text-white rounded-xl text-sm font-medium shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  ⏹️
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Query History */}
        <div className="flex-1 overflow-hidden flex flex-col">
          <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50 dark:bg-gray-700/50">
            <h3 className="font-semibold text-gray-800 dark:text-gray-200 text-sm">📚 Historique</h3>
            <div className="flex items-center gap-2">
              {(pinnedQueries.length > 0 && queryHistory.length > 0) && (
                <div className="relative group">
                  <button className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors p-1">
                    🗑️ Effacer
                  </button>
                  <div className="absolute right-0 top-6 mt-1 w-48 bg-white dark:bg-gray-800 shadow-lg rounded-md border border-gray-200 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
                    <div className="py-1">
                      {pinnedQueries.length > 0 && (
                        <>
                          <button
                            onClick={clearPinnedQueries}
                            className="w-full text-left px-4 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                          >
                            📌 les épinglées
                          </button>
                          <button
                            onClick={clearNotPinnedQueries}
                            className="w-full text-left px-4 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                          >
                            📌 les non épinglées
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {pinnedQueries.length === 0 && queryHistory.length > 0 && (
                <button
                  onClick={clearHistory}
                  className="w-full text-left px-4 py-2 text-xs text-gray-700 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                >
                  🗑️ Effacer l'historique
                </button>
              )}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto bg-gray-50/50 dark:bg-gray-800/50">
            {queryHistory.length === 0 && pinnedQueries.length === 0 ? (
              <div className="p-6 text-center text-gray-500 dark:text-gray-400 text-sm">
                <div className="text-4xl mb-2">📝</div>
                <p>Aucun historique</p>
                <p className="text-xs mt-1">Vos requêtes s'afficheront ici</p>
              </div>
            ) : (
              <div className="space-y-3 p-4">
                {/* Pinned Queries Section */}
                {pinnedQueries.length > 0 && (
                  <>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                        📌 Épinglées ({pinnedQueries.length})
                      </div>
                      <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600"></div>
                    </div>
                    {pinnedQueries.map((item) => (
                      <div
                        key={`pinned-${item.id}`}
                        className="relative group p-4 rounded-xl border-2 border-yellow-300 dark:border-yellow-600 bg-yellow-50 dark:bg-yellow-900/20 hover:border-yellow-400 dark:hover:border-yellow-500 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md"
                      >
                        <div
                          onClick={() => loadFromHistory(item)}
                          className="flex-1"
                        >
                          <div className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2 line-clamp-2 pr-8">
                            {item.question}
                          </div>
                          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                            <span className="flex items-center gap-1">
                              🕒 {new Date(item.timestamp).toLocaleDateString('fr-FR')}
                            </span>
                            <div className="flex items-center gap-2">
                              {item.hasChart && <span className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 px-2 py-1 rounded-full text-xs">📊 Graph</span>}
                              {item.rowCount > 0 && <span className="bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 px-2 py-1 rounded-full text-xs">{item.rowCount} lignes</span>}
                            </div>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            togglePin(item);
                          }}
                          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-yellow-200 dark:hover:bg-yellow-800 rounded-full"
                          title="Désépingler"
                        >
                          📌
                        </button>
                      </div>
                    ))}
                    {queryHistory.length > 0 && (
                      <div className="flex items-center gap-2 my-4">
                        <div className="text-xs font-medium text-gray-600 dark:text-gray-400 uppercase tracking-wide">
                          📋 Récentes ({queryHistory.length})
                        </div>
                        <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600"></div>
                      </div>
                    )}
                  </>
                )}

                {/* Regular History */}
                {queryHistory.map((item) => (
                  <div
                    key={item.id}
                    className="relative group p-4 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 hover:border-blue-300 dark:hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 cursor-pointer transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <div
                      onClick={() => loadFromHistory(item)}
                      className="flex-1"
                    >
                      <div className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2 line-clamp-2 pr-8">
                        {item.question}
                      </div>
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1">
                          🕒 {new Date(item.timestamp).toLocaleDateString('fr-FR')}
                        </span>
                        <div className="flex items-center gap-2">
                          {item.hasChart && <span className="bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 px-2 py-1 rounded-full text-xs">📊 Graph</span>}
                          {item.rowCount > 0 && <span className="bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 px-2 py-1 rounded-full text-xs">{item.rowCount} lignes</span>}
                        </div>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePin(item);
                      }}
                      className={`absolute top-2 right-2 transition-opacity p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full ${
                        isPinned(item)
                          ? 'opacity-100 text-yellow-600 dark:text-yellow-400'
                          : 'opacity-0 group-hover:opacity-100 text-gray-400 dark:text-gray-500'
                      }`}
                      title={isPinned(item) ? "Désépingler" : "Épingler"}
                    >
                      📌
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Column - Results & Progress */}
      <div className="flex-1 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden">
        {/* Results Area */}
        <div className="flex-1 p-6 overflow-y-auto">
          {isProcessing && !finalResult && <ResultSkeleton />}

          {/* Final Results */}
          {finalResult && (
            <div className="space-y-6">
              {/* Chart Display */}
              {finalResult.chart_data && (
                <div className="bg-white dark:bg-gray-700 rounded-xl shadow-sm border border-gray-200 dark:border-gray-600 p-6">
                  <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
                    📊 Visualisation
                  </h3>
                  <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-4" style={{ height: '400px' }}>
                    <Chart {...finalResult.chart_data.config} />
                  </div>
                </div>
              )}

              {/* Data Table */}
              {finalResult.data && finalResult.data.length > 0 && (() => {
                const columns = generateColumns(finalResult.data);

                if (columns.length === 0) {
                  return (
                    <div className="bg-white dark:bg-gray-700 rounded-xl shadow-sm border border-gray-200 dark:border-gray-600 p-6">
                      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200 mb-4">
                        📋 Données ({finalResult.data.length} lignes)
                      </h3>
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <p>Impossible de générer les colonnes pour afficher les données.</p>
                        <pre className="mt-4 text-xs bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 p-4 rounded-xl overflow-auto text-left">
                          {JSON.stringify(finalResult.data.slice(0, 2), null, 2)}
                        </pre>
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="bg-white dark:bg-gray-700 rounded-xl shadow-sm border border-gray-200 dark:border-gray-600 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-semibold text-gray-800 dark:text-gray-200">
                        📋 Données ({finalResult.data.length} lignes)
                      </h3>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {finalResult.metadata?.execution_time_ms && (
                          <span className="bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 px-2 py-1 rounded-full text-xs">
                            ⚡ {finalResult.metadata.execution_time_ms}ms
                          </span>
                        )}
                      </div>
                    </div>

                    <SortableTable
                      data={finalResult.data}
                      columns={columns}
                      maxHeight="500px"
                      globalSearch={true}
                      exportable={true}
                      exportFilename={`ai_query_${Date.now()}.csv`}
                      exportRenamable={true}
                    />
                  </div>
                );
              })()}

            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-red-600 dark:text-red-400 text-xl">❌</span>
                <h3 className="font-semibold text-red-800 dark:text-red-400">Erreur</h3>
              </div>
              <p className="text-red-700 dark:text-red-300 text-sm">{error.message}</p>
            </div>
          )}
        </div>

                {/* Progress Bar - Bottom */}
        {(isProcessing || streamingSteps.length > 0) && (
          <div className="border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-medium text-gray-800 dark:text-gray-200 text-sm flex items-center gap-2">
                ⚡ Progression
              </h4>
              <button
                onClick={() => toggleStepExpansion('progress_details')}
                className="text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 transition-colors"
              >
                {expandedSteps.has('progress_details') ? '👁️ Masquer détails' : '🔍 Voir détails'}
              </button>
            </div>
            {/* Compact Progress Steps */}
            <div className="flex items-center gap-2 mb-3 overflow-x-auto">
              {/* Determine which steps to show based on what we've received */}
              {(() => {
                const receivedSteps = streamingSteps.map(s => s.step);
                let stepsToShow = ['analysis'];

                if (receivedSteps.includes('sql_generation')) {
                  stepsToShow = ['analysis', 'sql_generation', 'execution', 'completion'];
                } else if (receivedSteps.includes('multi_query_planning')) {
                  stepsToShow = ['analysis', 'multi_query_planning', 'multi_query_execution', 'data_merging', 'completion'];
                } else if (receivedSteps.includes('complex_analysis')) {
                  stepsToShow = ['analysis', 'complex_analysis', 'execution', 'completion'];
                } else {
                  stepsToShow = ['analysis', 'execution', 'completion'];
                }

                return stepsToShow.map((stepName, index) => {
                  const step = streamingSteps.find(s => s.step === stepName);
                  const isActive = step?.status === 'running';
                  const isCompleted = step?.status === 'completed';
                  const isFailed = step?.status === 'failed';

                  return (
                    <div key={stepName} className="flex items-center flex-shrink-0">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-medium shadow-sm transition-all duration-300 ${
                        isFailed ? 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-400 border-2 border-red-300 dark:border-red-700' :
                        isCompleted ? 'bg-green-100 dark:bg-green-900 text-green-600 dark:text-green-400 border-2 border-green-300 dark:border-green-700' :
                        isActive ? 'bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-400 border-2 border-blue-300 dark:border-blue-700 animate-pulse' :
                        'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 border-2 border-gray-200 dark:border-gray-600'
                      }`}>
                        {isFailed ? '✕' : isCompleted ? '✓' : isActive ? '⏳' : index + 1}
                      </div>
                      {index < stepsToShow.length - 1 && (
                        <div className={`w-8 h-1 rounded-full transition-all duration-300 ${
                          isCompleted ? 'bg-green-300 dark:bg-green-600' : 'bg-gray-200 dark:bg-gray-600'
                        }`}></div>
                      )}
                    </div>
                  );
                });
              })()}

              {/* Current Step Message - Inline */}
              {streamingSteps.length > 0 && (
                <div className="ml-3 text-xs text-gray-500 dark:text-gray-500">
                  {streamingSteps[streamingSteps.length - 1]?.message}
                </div>
              )}
            </div>

            {/* Detailed Steps - Expandable */}
            {expandedSteps.has('progress_details') && streamingSteps.length > 0 && (
              <div className="mt-4 space-y-3 max-h-60 overflow-y-auto bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3">
                {streamingSteps.map((step, index) => (
                  <div key={`${step.step}-${index}`} className="flex items-start gap-3 text-sm bg-white dark:bg-gray-700 p-3 rounded-lg shadow-sm">
                    <span className="text-lg">{getStepIcon(step.step, step.status)}</span>
                    <div className="flex-1">
                      <div className="font-medium text-gray-800 dark:text-gray-200">{getStepTitle(step.step)}</div>
                      <div className="text-gray-600 dark:text-gray-400">{step.message}</div>
                      {step.data && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 bg-gray-100 dark:bg-gray-600 p-2 rounded">
                          {step.step === 'execution' && step.data.row_count !== undefined && (
                            <span className="font-mono">📊 {step.data.row_count} lignes • ⚡ {step.data.execution_time_ms}ms</span>
                          )}
                          {step.step === 'analysis' && step.data.query_strategy && (
                            <span className="font-mono">🎯 Stratégie: {step.data.query_strategy}</span>
                          )}
                          {step.step === 'sql_generation' && step.data.query && (
                            <div>
                              <div className="font-medium text-gray-700 dark:text-gray-300 text-xs mb-1">
                                📝 Requête SQL générée:
                              </div>
                              <pre className="bg-gray-200 dark:bg-gray-500 text-gray-800 dark:text-gray-200 p-2 rounded text-xs overflow-x-auto whitespace-pre-wrap">
                                {step.data.query}
                              </pre>
                            </div>
                          )}
                          {step.step === 'chart_preparation' && step.data && (
                            <div>
                              <div className="font-medium text-gray-700 dark:text-gray-300 text-xs mb-1">
                                📊 Informations de préparation du graphique:
                              </div>
                              <div className="space-y-1">
                                {step.data.chart_type && (
                                  <div className="font-mono">Type: {step.data.chart_type}</div>
                                )}
                                {step.data.transformation && (
                                  <div className="font-mono">Transformation: {step.data.transformation}</div>
                                )}
                                {step.data.analysis && step.data.analysis.reason && (
                                  <div className="font-mono">Méthode: {step.data.analysis.reason}</div>
                                )}
                                {step.data.analysis && step.data.analysis.detectedColumns && (
                                  <div className="text-xs mt-1">
                                    <div>Colonnes détectées:</div>
                                    <div className="ml-2">
                                      • Date: {step.data.analysis.detectedColumns.dateColumn || 'N/A'}
                                    </div>
                                    <div className="ml-2">
                                      • Série: {step.data.analysis.detectedColumns.seriesColumn || 'N/A'}
                                    </div>
                                    <div className="ml-2">
                                      • Valeur: {step.data.analysis.detectedColumns.valueColumn || 'N/A'}
                                    </div>
                                  </div>
                                )}
                                {step.data.metadata && step.data.metadata.seriesCount && (
                                  <div className="font-mono">Séries: {step.data.metadata.seriesCount}</div>
                                )}
                              </div>
                            </div>
                          )}
                          {step.step === 'multi_query_planning' && step.data.queries_count && (
                            <div>
                              <span className="font-mono">📋 {step.data.queries_count} requêtes planifiées</span>
                              {step.data.queries && step.data.queries.length > 0 && (
                                <div className="mt-2 space-y-1">
                                  {step.data.queries.map((query, qIndex) => (
                                    <div key={qIndex}>
                                      <div className="font-medium text-gray-700 dark:text-gray-300 text-xs">
                                        Requête {qIndex + 1}:
                                      </div>
                                      <pre className="bg-gray-200 dark:bg-gray-500 text-gray-800 dark:text-gray-200 p-1 rounded text-xs overflow-x-auto whitespace-pre-wrap">
                                        {query}
                                      </pre>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          {step.step === 'multi_query_execution' && step.data.current_query && (
                            <span className="font-mono">🔄 Requête {step.data.current_query}/{step.data.total_queries}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Show SQL queries if available */}
                {finalResult && finalResult.sql_queries && finalResult.sql_queries.length > 0 && (
                  <div className="bg-white dark:bg-gray-700 p-3 rounded-lg shadow-sm">
                    <div className="font-medium text-gray-800 dark:text-gray-200 mb-2">🔍 Requêtes SQL générées</div>
                    <div className="space-y-2">
                      {finalResult.sql_queries.map((query, index) => (
                        <div key={index} className="text-xs">
                          <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Requête {index + 1}/{finalResult.sql_queries.length}:
                          </div>
                          <pre className="bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-gray-200 p-2 rounded text-xs overflow-x-auto whitespace-pre-wrap">
                            {query}
                          </pre>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIStreamingQuery;